import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';

const COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#14b8a6',
  '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
  '#f43f5e', '#6b7280'
];

function Tags() {
  const { tags, fetchTags, createTag, updateTag, deleteTag } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTag, setNewTag] = useState({ name: '', color: '#6366f1' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []); // Only fetch once on mount

  const handleCreate = async () => {
    if (!newTag.name.trim()) return;

    setSaving(true);
    try {
      await createTag(newTag);
      setNewTag({ name: '', color: '#6366f1' });
      setShowForm(false);
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, data) => {
    setSaving(true);
    try {
      await updateTag(id, data);
      setEditingId(null);
    } catch (error) {
      console.error('Error updating tag:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tag? It will be removed from all contacts.')) return;

    try {
      await deleteTag(id);
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
            Tags
          </h1>
          <p className="text-sm text-muted">
            Organize contacts with custom tags
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          <Plus size={18} />
          Add Tag
        </button>
      </div>

      {/* New Tag Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
              <label className="form-label">Tag Name</label>
              <input
                type="text"
                value={newTag.name}
                onChange={(e) => setNewTag(prev => ({ ...prev, name: e.target.value }))}
                className="form-input"
                placeholder="e.g., VIP, Newsletter, Local"
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Color</label>
              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', maxWidth: '200px' }}>
                {COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewTag(prev => ({ ...prev, color }))}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      background: color,
                      border: newTag.color === color ? '2px solid var(--gray-900)' : '2px solid transparent',
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowForm(false);
                  setNewTag({ name: '', color: '#6366f1' });
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreate}
                disabled={saving || !newTag.name.trim()}
              >
                {saving ? 'Saving...' : 'Create Tag'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tags List */}
      {tags.length === 0 ? (
        <div className="card empty-state">
          <p>No tags created yet</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => setShowForm(true)}
          >
            Create Your First Tag
          </button>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Contacts</th>
                  <th style={{ width: '120px' }}></th>
                </tr>
              </thead>
              <tbody>
                {tags.map(tag => (
                  <tr key={tag.id}>
                    <td>
                      {editingId === tag.id ? (
                        <EditTagRow
                          tag={tag}
                          onSave={(data) => handleUpdate(tag.id, data)}
                          onCancel={() => setEditingId(null)}
                          saving={saving}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '6px',
                              background: tag.color
                            }}
                          />
                          <span className="font-medium">{tag.name}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-primary">
                        {tag.contact_count || 0} contact{(tag.contact_count || 0) !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td>
                      {editingId !== tag.id && (
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setEditingId(tag.id)}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleDelete(tag.id)}
                            style={{ color: 'var(--danger)' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function EditTagRow({ tag, onSave, onCancel, saving }) {
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color);
  const [showColors, setShowColors] = useState(false);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setShowColors(!showColors)}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            background: color,
            border: 'none',
            cursor: 'pointer'
          }}
        />
        {showColors && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '0.5rem',
            padding: '0.5rem',
            background: 'white',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            gap: '0.25rem',
            flexWrap: 'wrap',
            width: '180px',
            zIndex: 10
          }}>
            {COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setColor(c);
                  setShowColors(false);
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  background: c,
                  border: color === c ? '2px solid var(--gray-900)' : 'none',
                  cursor: 'pointer'
                }}
              />
            ))}
          </div>
        )}
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="form-input"
        style={{ width: '150px', padding: '0.375rem 0.5rem' }}
      />
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => onSave({ name, color })}
        disabled={saving || !name.trim()}
        style={{ color: 'var(--success)' }}
      >
        <Check size={16} />
      </button>
      <button
        className="btn btn-ghost btn-sm"
        onClick={onCancel}
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default Tags;
